import React from 'react'
import styled from 'styled-components'
import { globalState } from '../../store'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faUserSecret, faSignOutAlt, faExclamationTriangle } from '@fortawesome/pro-duotone-svg-icons'

const BannerWrapper = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1100; /* Above header */
  background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
  color: white;
  padding: 10px 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  font-size: 14px;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 10px;
    padding: 12px 16px;
  }
`

const BannerContent = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`

const UserInfo = styled.span`
  font-weight: 600;
`

const ReadOnlyBadge = styled.span`
  background: rgba(255, 255, 255, 0.2);
  padding: 4px 10px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 6px;
`

const ExitButton = styled.button`
  background: white;
  color: #dc2626;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s ease;

  &:hover {
    background: #fef2f2;
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`

const OriginalUserLabel = styled.span`
  opacity: 0.9;
  font-size: 13px;
`

export const ImpersonationBanner = () => {
  const {
    state: { isImpersonating, impersonatedUser, originalUser, csrf },
    dispatch,
  } = globalState()

  if (!isImpersonating || !impersonatedUser) {
    return null
  }

  const handleExit = async () => {
    try {
      const response = await fetch('/api/rbac/impersonate/release/', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'X-CSRFToken': csrf,
          'Content-Type': 'application/json',
        },
      })

      const data = await response.json()

      if (response.ok && data.success) {
        // Clear impersonation state
        dispatch({ type: 'clearImpersonation' })
        // Redirect to the appropriate dashboard for the original user
        window.location.href = data.redirect_url || '/company/dashboard/'
      } else {
        console.error('Failed to exit impersonation:', data.error)
      }
    } catch (e) {
      console.error('Error exiting impersonation:', e)
    }
  }

  return (
    <BannerWrapper data-testid="impersonation-banner">
      <BannerContent>
        <FontAwesomeIcon icon={faUserSecret} size="lg" />
        <span>
          Viewing as <UserInfo>{impersonatedUser.full_name || impersonatedUser.email}</UserInfo>
        </span>
        {originalUser && <OriginalUserLabel>(logged in as {originalUser.email})</OriginalUserLabel>}
      </BannerContent>

      <ReadOnlyBadge>
        <FontAwesomeIcon icon={faExclamationTriangle} />
        Read-only Mode
      </ReadOnlyBadge>

      <ExitButton onClick={handleExit} data-testid="exit-impersonation-btn">
        <FontAwesomeIcon icon={faSignOutAlt} />
        Exit Impersonation
      </ExitButton>
    </BannerWrapper>
  )
}

// Spacer component to push content down when banner is visible
export const ImpersonationBannerSpacer = () => {
  const {
    state: { isImpersonating },
  } = globalState()

  if (!isImpersonating) {
    return null
  }

  return <div style={{ height: '52px' }} />
}

export default ImpersonationBanner
